import React, { useEffect, useState, useRef } from 'react'
import { AlertTriangle, Clock } from 'lucide-react'
import MaskPreview from '../../MaskPreview'
import Breadcrumb from '../shared/Breadcrumb'
import useMaskStore from '../../../../stores/maskStore'
import masksApi from '../../../../services/masksApi'
import { Spinner } from '../../../ui/spinner'

const MaskList = ({
  generationId,
  versionId,
  selectedMaskId,
  showMaskPreview,
  onMaskSelect,
  onPreviewToggle,
  onUseMask,
  onBack,
  onNavigate
}) => {
  const { masks, isLoading, error, nextRetryTime } = useMaskStore()
  const [retryCountdown, setRetryCountdown] = useState(null)

  // Add countdown timer effect
  useEffect(() => {
    if (!nextRetryTime) {
      setRetryCountdown(null)
      return
    }

    const updateCountdown = () => {
      const now = Date.now()
      const remaining = Math.max(0, Math.ceil((nextRetryTime - now) / 1000))
      setRetryCountdown(remaining)
    }

    // Update immediately
    updateCountdown()

    // Update every second
    const intervalId = setInterval(updateCountdown, 1000)

    return () => clearInterval(intervalId)
  }, [nextRetryTime])

  useEffect(() => {
    // Only fetch masks if we have both IDs
    if (generationId && versionId) {
      masksApi.listMasks(generationId, versionId)
    }
  }, [generationId, versionId])

  // Add ref for the preview container
  const selectedPreviewRef = useRef(null)

  // Add scroll into view effect when mask is selected
  useEffect(() => {
    if (selectedMaskId && selectedPreviewRef.current) {
      // Small delay to ensure preview has rendered
      setTimeout(() => {
        selectedPreviewRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        })
      }, 100)
    }
  }, [selectedMaskId])

  const breadcrumbItems = [
    { id: 'tools', label: 'Tools' },
    { id: 'inpainting', label: 'Generative Inpainting' },
    { id: 'existing-masks', label: 'Existing Masks' }
  ]

  if (error) {
    return (
      <div className="flex flex-col space-y-6 mt-4">
        <Breadcrumb items={breadcrumbItems} onNavigate={onNavigate} />
        <div className="flex flex-col items-center gap-2 text-center py-4">
          <div className="text-red-400 text-sm">
            {error}
          </div>
          {retryCountdown !== null && (
            <div className="text-gray-500 text-xs">
              Retrying in {retryCountdown}s
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col space-y-6 mt-4">
      <Breadcrumb items={breadcrumbItems} onNavigate={onNavigate} />

      <div className="space-y-4">
        {masks.map(mask => (
          <div key={mask._id} className="space-y-4">
            <div key={`container-${mask._id}`} className="flex flex-col">
              <button
                key={`button-${mask._id}`}
                onClick={() => {
                  if (mask.status === 'completed') {
                    onMaskSelect(selectedMaskId === mask._id ? null : mask._id)
                  }
                }}
                disabled={!mask.status}
                className={`w-full px-5 py-4 rounded-lg transition-colors text-left
                  ${mask.status === 'generating' 
                    ? 'bg-gray-700/50 cursor-not-allowed' 
                    : mask.status === 'failed' 
                      ? 'bg-gray-700/50 cursor-not-allowed opacity-75' 
                      : selectedMaskId === mask._id 
                        ? 'bg-blue-600/20 border border-blue-500' 
                        : mask.status === 'completed' 
                          ? 'bg-gray-700 hover:bg-gray-600' 
                          : 'bg-gray-700/50 cursor-not-allowed opacity-75'}`}
              >
                <div key={`header-${mask._id}`} className="flex justify-between items-center mb-2.5">
                  <div key={`title-${mask._id}`} className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-2.5">
                      <span className="font-medium">{mask.name}</span>
                      {mask.status === 'generating' && (
                        <div className="flex items-center gap-2 text-blue-400">
                          <Spinner className="text-blue-400" />
                          <span>Generating...</span>
                        </div>
                      )}
                      {mask.status === 'failed' && (
                        <AlertTriangle key={`failed-${mask._id}`} className="w-4 h-4 text-red-400" />
                      )}
                    </div>
                    {mask.status === 'generating' && mask.estimatedWait && (
                      <div key={`time-estimate-${mask._id}`} className="flex items-center gap-1.5 text-gray-400">
                        <Clock className="w-3.5 h-3.5" />
                        <span className="text-sm">
                          Estimated time: {mask.estimatedWait}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div key={`info-${mask._id}`} className="text-sm text-gray-400 flex items-center gap-2.5">
                  <span>
                    {mask.type === 'description' ? 'Text-based mask' : 'Brush mask'}
                  </span>
                  {mask.prompt && (
                    <span className="text-sm truncate text-gray-500">
                      • {mask.prompt}
                    </span>
                  )}
                </div>
              </button>

              {/* Only show preview for completed masks that are selected */}
              {selectedMaskId === mask._id && mask.status === 'completed' && (
                <div 
                  ref={selectedPreviewRef}
                  className="mt-4 space-y-4"
                >
                  <div className="rounded-lg overflow-hidden bg-gray-800/50 border border-gray-700">
                    <MaskPreview 
                      maskId={mask._id}
                      className="w-full h-40 object-contain"
                    />
                    {mask.type === 'description' && mask.prompt && (
                      <div key={`prompt-container-${mask._id}`} className="px-5 py-4 border-t border-gray-700/75">
                        <div key={`prompt-${mask._id}`} className="text-sm text-gray-400">
                          <span className="font-medium text-gray-300">Prompt: </span>
                          {mask.prompt}
                        </div>
                        {mask.negativePrompt && (
                          <div key={`negative-prompt-${mask._id}`} className="text-sm text-gray-400 mt-2">
                            <span className="font-medium text-gray-300">Negative: </span>
                            {mask.negativePrompt}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Preview and Apply buttons */}
                  <div key={`buttons-${mask._id}`} className="flex gap-3">
                    <button
                      key={`preview-button-${mask._id}`}
                      onClick={() => onPreviewToggle(!showMaskPreview)}
                      className="flex-1 px-5 py-2.5 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors font-medium"
                    >
                      {showMaskPreview ? 'Hide Preview' : 'Show Preview'}
                    </button>
                    <button
                      key={`apply-button-${mask._id}`}
                      onClick={() => {
                        if (mask.status === 'completed') {
                          onUseMask()
                        }
                      }}
                      disabled={!mask.status}
                      className="flex-1 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors font-medium"
                    >
                      Apply Mask
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {masks.length === 0 && !isLoading && (
          <div key="empty-state" className="flex flex-col gap-4">
            <div key="empty-message" className="text-sm text-gray-400 text-center py-6">
              No masks created yet
            </div>

            <button
              key="create-new"
              onClick={() => onNavigate('inpainting')}
              className="w-full py-2.5 px-5 bg-gray-700 rounded-lg hover:bg-gray-600 font-medium transition-colors"
            >
              Create New Mask
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default MaskList 